import {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {Card, Nav} from 'react-bootstrap';
import Button from '../../../../../Components/Button';
import Loading from '../../../../../app/common/components/Loading';
import {
  useGetTeamsRecord,
  useGetVirtualPbxActivite,
  useSaveTeamsRecord,
} from '../../../Hooks/TelephonieManager';

/**
 * MAQUETTE — reproduction de « ManageTeams.aspx » : assistant à 5 étapes (navigation
 * libre par onglets) pour déclarer le domaine Teams et l'enregistrement DNS TXT de
 * vérification. Seule l'étape 3 (lecture/édition du TXT) a une vraie donnée métier ;
 * les autres étapes sont du contenu guidé (texte + captures, non reproduites ici).
 */

type Etape = 1 | 2 | 3 | 4 | 5;

const ETAPES: Etape[] = [1, 2, 3, 4, 5];

interface GererTeamsProps {
  onFermer: () => void;
}

const GererTeams = ({onFermer}: GererTeamsProps): JSX.Element | null => {
  const intl = useIntl();
  const {data: activiteData, isLoading: isLoadingActivite} = useGetVirtualPbxActivite();
  const {data: teamsData, isLoading: isLoadingTeams} = useGetTeamsRecord();
  const {mutate: enregistrerTxt} = useSaveTeamsRecord();
  const [etape, setEtape] = useState<Etape>(1);
  const [modeEdition, setModeEdition] = useState(false);
  const [valeurEditee, setValeurEditee] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (teamsData) {
      setValeurEditee(teamsData.txtRecord);
      setModeEdition(teamsData.txtRecord.trim() === '');
    }
  }, [teamsData]);

  if (isLoadingActivite || !activiteData || isLoadingTeams || !teamsData) {
    return <Loading className="col-md-6" />;
  }

  const domaine = activiteData.teamsAssocie?.domaine ?? '';

  const validerEtEnregistrer = (): void => {
    if (valeurEditee.trim() === '') {
      setErreur(
        intl.formatMessage(
          {id: 'TELEPHONIE.PBX.TEAMS.ETAPE3.ERREUR_OBLIGATOIRE'},
          {champs: intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE3.TXT_LABEL'})}
        )
      );
      return;
    }
    setErreur(null);
    enregistrerTxt(valeurEditee.trim());
    setModeEdition(false);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold text-uppercase m-0">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.TITLE'})}
        </h3>
        <Button
          label={intl.formatMessage({id: 'COMMON.BACK'})}
          faIcon="bi bi-arrow-left fs-6 me-2"
          onClick={onFermer}
        />
      </div>
      <p className="fs-6 mb-5">{intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.DESCRIPTION'})}</p>

      <Nav variant="tabs" className="mb-5">
        {ETAPES.map((numero) => (
          <Nav.Item key={numero}>
            <Nav.Link active={etape === numero} onClick={() => setEtape(numero)}>
              {intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE_N'}, {n: numero})}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {etape === 1 && (
        <Card bg="light">
          <Card.Body>
            <p className="fs-6">{intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE1.TEXTE'})}</p>
          </Card.Body>
        </Card>
      )}

      {etape === 2 && (
        <Card bg="light">
          <Card.Body>
            <p className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE2.TEXTE'}, {domaine})}
            </p>
          </Card.Body>
        </Card>
      )}

      {etape === 3 && (
        <Card bg="light">
          <Card.Body>
            <p className="fs-6 mb-4">{intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE3.TEXTE'})}</p>

            {erreur && <div className="fw-bold text-danger mb-3">{erreur}</div>}

            <div className="fw-bold fs-6 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE3.TXT_LABEL'})}
            </div>
            {modeEdition ? (
              <div className="d-flex flex-wrap align-items-center gap-3">
                <input
                  className="form-control"
                  style={{maxWidth: 320}}
                  value={valeurEditee}
                  onChange={(e) => setValeurEditee(e.target.value)}
                />
                <Button
                  label={intl.formatMessage({id: 'SAVE'})}
                  onClick={validerEtEnregistrer}
                />
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold fs-6">{teamsData.txtRecord}</span>
                <i
                  className="bi bi-pencil-fill text-primary cursor-pointer"
                  onClick={() => {
                    setValeurEditee(teamsData.txtRecord);
                    setModeEdition(true);
                  }}
                />
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {etape === 4 && (
        <Card bg="light">
          <Card.Body>
            <p className="fs-6">{intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE4.TEXTE'})}</p>
          </Card.Body>
        </Card>
      )}

      {etape === 5 && (
        <Card bg="light">
          <Card.Body>
            <p className="fs-6">{intl.formatMessage({id: 'TELEPHONIE.PBX.TEAMS.ETAPE5.TEXTE'})}</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default GererTeams;
